import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { TicketingEvent } from '@wbk/api';
import { Button, Typography } from '@wbk/ui';
import { CurrencyPriceLabel } from '@wbk/currency';
import { useQueue } from '@wbk/queue';
import type { getEventStatus } from '@/util/events';

type Props = {
  event: TicketingEvent;
  eventStatus: ReturnType<typeof getEventStatus>;
  className?: string;
};

const BuyTicketButton = ({ event, eventStatus, className }: Props) => {
  const { locale, asPath, query } = useRouter();
  const { t } = useTranslation();
  const { isActive, shouldWaitQueue } = useQueue();

  const isPrivate = query.private === 'true';

  const externalLink = event.tickets_external_link[locale || ''];
  const withQueue = process.env.NEXT_PUBLIC_QUEUE === 'true';
  const BuyTag = eventStatus.disabled ? 'button' : externalLink || withQueue ? 'a' : Link;
  const currentPath = asPath.split('?')[0];

  if (shouldWaitQueue) {
    return <Button className='min-h-[50px] w-full animate-pulse opacity-30' disabled></Button>;
  }

  if (isActive) {
    return null;
  }

  return (
    <Button
      as={BuyTag}
      href={externalLink || isPrivate ? `${currentPath}/book?private=true` : `${currentPath}/book`}
      theme='primary'
      disabled={eventStatus.disabled}
      className={`min-h-[50px] w-full gap-2 text-lg ring-offset-secondary ${className || ''}`}
      data-event-name={event.title}
      data-event-city={event.city}
      data-event-start-date={event.start_date_time_str}
      data-venue={event.venue_name}
      data-category='main'
    >
      <div>
        <strong className='capitalize leading-none'>{t(`event:${eventStatus.message}`)}</strong>
        {event.event_start_price > 0 && (
          <div className='flex items-center gap-1'>
            <Typography variant='body-S'>{t('event:starts_from')}</Typography>
            <CurrencyPriceLabel
              originalPrice={event.event_start_price + event.event_start_price_vat}
              currency={event.currency}
              priceClassName='text-sm'
            />
          </div>
        )}
      </div>

      {externalLink && (
        <Image
          className='ltr:-mt-0.5 rtl:-rotate-90'
          src='/icons/common/external-arrow.svg'
          width={16}
          height={16}
          alt={t(`event:${eventStatus.message}`).toUpperCase()}
        />
      )}
    </Button>
  );
};

export default BuyTicketButton;
